/* eslint-disable no-nested-ternary */
import React, { useMemo } from 'react';
import moment from 'moment';
import { LockRounded } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@src/languages';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@mui/material';
import BadgeAvatar from '@src/components/BadgeAvatar';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import actions from '@src/redux/actions';
import useCustomSSO from '@src/hooks/useCustomSSO';
import useUserInfo from '@src/hooks/useTtsUser';
import {
  checkAutoRenewalPackage,
  checkCustomPackage,
  getDefaultValueCustomResource,
  getPackageName,
  getSMSPackageName,
} from '@src/services/package';
import { delimitNumber } from '@src/utils/number';
import { SMS_IDENTITY_PROVIDER } from '@src/constants';
import { SUBSCRIPTION_PURCHASE_STATUS } from '@src/constants/subscription';
import {
  FREE_PACKAGE_CODES,
  PACKAGE_LEVEL,
  PACKAGE_STUDIO_VERSION,
  PACKAGE_TYPE,
} from '@src/constants/package';
import route from '@src/constants/route';
import { VBEE_CONSOLE_URL } from '@src/configs';
import { NewUserInformationNoteWrapper } from './index.style';

const checkExpiredDate = (expiryDate) => {
  if (!expiryDate) return false;
  return moment().isAfter(expiryDate);
};

const FORMAT_DATETIME = 'DD/MM/YYYY - HH:mm';

const MultipleCreditInfo = ({ isShowUpgradeButton }) => {
  const { keycloak } = useCustomSSO();
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const { usingPackage, lastExpiredOrder, subscriptionPurchase } = useSelector(
    (state) => state.user,
  );

  const {
    remainingCharacters = 0,
    bonusCharacters = 0,
    lockCharacters = 0,
    identityProvider,
  } = useSelector((state) => state.auth.user);
  const { ttsUser } = useUserInfo();
  const { studio } = ttsUser;
  const { cycle, topUp, custom } = studio || {};
  const { remainingCredits: remainingCycleCredits = 0, nextResetAt } =
    cycle || {};
  const { remainingCredits: remainingTopUpCredits = 0 } = topUp || {};
  const { remainingCredits: remainingCustomCredits = 0 } = custom || {};

  const isSMSUser = Object.values(SMS_IDENTITY_PROVIDER).includes(
    identityProvider,
  );

  const userUseOneTimePackage = useMemo(() => {
    const packageVersion = usingPackage?.version || PACKAGE_STUDIO_VERSION.V1;
    return [
      PACKAGE_STUDIO_VERSION.V1,
      PACKAGE_STUDIO_VERSION.V2_1,
      PACKAGE_STUDIO_VERSION.V2,
    ].includes(packageVersion);
  }, [usingPackage]);

  const userUseCustomPackage = useMemo(() => {
    const packageLevel = usingPackage?.level || '';
    return packageLevel === PACKAGE_LEVEL.CUSTOM;
  }, [usingPackage]);

  const { language } = i18n;
  const packageName = usingPackage.id
    ? getPackageName(
        usingPackage.name,
        usingPackage.code,
        language,
        t,
        true,
        true,
      )
    : getPackageName(
        lastExpiredOrder?.package?.name,
        user.packageCode,
        language,
        t,
        true,
        true,
      );

  const smsPackageName = usingPackage.id
    ? getSMSPackageName({ name: usingPackage?.name, language })
    : '';

  const { packageExpiryDate } = user;
  const isExpiredPackage = checkExpiredDate(packageExpiryDate);

  const totalOldRemainingCharacters =
    remainingCharacters + bonusCharacters || 0;

  // this below code is the logic to render button
  // clone from src/containers/Subcription/
  // if have time => use redux and refactor as an utils function

  const { type, code, level = '' } = usingPackage;
  const isAutoRenewalPackage = checkAutoRenewalPackage(usingPackage);
  const isFreePackage = FREE_PACKAGE_CODES.includes(code);
  const isCustomPackage = checkCustomPackage({ type, level });

  const {
    defaultCredits = 0,
    defaultDevices = 0,
    defaultVoiceClonings = 0,
  } = getDefaultValueCustomResource(usingPackage?.customResource) || {};

  const handleRedirectToBuyPackage = () => {
    switch (type) {
      case PACKAGE_TYPE.STUDIO:
        // eslint-disable-next-line no-case-declarations
        let redirectRoute = route.PAYMENT;

        redirectRoute = isFreePackage
          ? redirectRoute
          : `${route.PAYMENT}?package=${code}`;

        redirectRoute = isCustomPackage
          ? `${route.PAYMENT}?package=${code}&credits=${defaultCredits}&devices=${defaultDevices}&voiceClonings=${defaultVoiceClonings}`
          : redirectRoute;
        history.push(redirectRoute);
        dispatch(actions.layout.handleChangeOpenSidebar(false));
        break;
      case PACKAGE_TYPE.API:
        window.open(
          isFreePackage
            ? `${VBEE_CONSOLE_URL}/payments`
            : `${VBEE_CONSOLE_URL}/payments?package=${code}`,
        );
        dispatch(actions.layout.handleChangeOpenSidebar(false));
        break;
      case PACKAGE_TYPE.DUBBING:
        history.push(
          isFreePackage
            ? route.PAYMENT_DUBBING
            : `${route.PAYMENT_DUBBING}?package=${code}`,
        );
        dispatch(actions.layout.handleChangeOpenSidebar(false));
        break;

      default:
        break;
    }
  };

  const handleNavigateRegisterAutoRenew = () => {
    if (type === PACKAGE_TYPE.STUDIO) {
      history.push(route.SUBSCRIPTION);
      dispatch(actions.layout.handleChangeOpenSidebar(false));
    }
  };

  const renderPackageButton = () => {
    const enableCancelAutoRenew =
      isAutoRenewalPackage &&
      subscriptionPurchase?.status &&
      subscriptionPurchase?.status !== SUBSCRIPTION_PURCHASE_STATUS.CANCELED;

    const enableRegisterAutoRenew =
      isAutoRenewalPackage &&
      (!subscriptionPurchase?.status ||
        subscriptionPurchase?.status === SUBSCRIPTION_PURCHASE_STATUS.CANCELED);

    if (enableCancelAutoRenew) return <></>;

    if (isAutoRenewalPackage && isExpiredPackage) {
      return (
        <Button className="button-upgrade" onClick={handleRedirectToBuyPackage}>
          {t('upgradeNow')}
        </Button>
      );
    }

    if (enableRegisterAutoRenew) {
      return (
        <Button
          onClick={handleNavigateRegisterAutoRenew}
          className="button-upgrade"
        >
          {t('registerAutoRenew')}
        </Button>
      );
    }

    if (isFreePackage || isExpiredPackage) {
      return (
        <Button className="button-upgrade" onClick={handleRedirectToBuyPackage}>
          {t('upgradeNow')}
        </Button>
      );
    }

    return (
      <Button className="button-upgrade" onClick={handleRedirectToBuyPackage}>
        {t('buyMore')}
      </Button>
    );
  };

  const getPackageExpired = () => {
    if (isExpiredPackage && !userUseOneTimePackage) return t('expired');
    if (isExpiredPackage && userUseOneTimePackage && lockCharacters === 0)
      return t('expired');

    if (!packageExpiryDate && code) return t('endless');

    if (nextResetAt) return moment(nextResetAt).format(FORMAT_DATETIME);

    if (packageExpiryDate)
      return moment(packageExpiryDate).format(FORMAT_DATETIME);

    return '';
  };

  const renderNoteLockCredits = () => (
    <div className="list-note">{t('contentTooltipLockCredits')}</div>
  );

  const renderContainerLockCredits = () => {
    const note = renderNoteLockCredits();
    const isLockedCycle =
      remainingCycleCredits && ttsUser?.studio?.cycle?.isLocked;
    const isLockedCustom =
      remainingCustomCredits && ttsUser?.studio?.custom?.isLocked;

    if (isLockedCycle || isLockedCustom)
      return (
        <div className="lock-cycle-notification">
          <LockRoundedIcon className="icon-lock-cycle" />
          {note}
        </div>
      );

    if (remainingTopUpCredits && ttsUser?.studio?.topUp?.isLocked)
      return (
        <div className="lock-cycle-notification">
          <LockRoundedIcon className="icon-lock-cycle" />
          {note}
        </div>
      );

    return <></>;
  };

  return (
    <NewUserInformationNoteWrapper>
      <div className="header-user-info">
        <div className="avatar">
          <BadgeAvatar
            width={32}
            img={user.avatar}
            active={keycloak && keycloak.authenticated}
            name={user.firstName || user.lastName}
            number={new Date(user.createdAt)}
            smallImgWidth={20}
            type="icon"
            removeDefaultIcon
          />
        </div>
        <div className="user-info-wrapper">
          <div className="username">
            {`${user?.firstName || ''} ${user?.lastName || ''}`}
          </div>
          <div className="package-info">
            {isSMSUser ? smsPackageName : packageName}
          </div>
        </div>
      </div>
      <div className="package-info-container">
        {/* cycle packages */}
        <div className="package-info-item">
          {!isFreePackage && !userUseOneTimePackage && !userUseCustomPackage && (
            <>
              <div className="package-info">{t('remainCycleCharacters')}</div>
              <div className="package-info-value">
                {isExpiredPackage
                  ? 0
                  : `${delimitNumber(
                      remainingCycleCredits +
                        remainingTopUpCredits +
                        bonusCharacters,
                      language,
                    )} ${t('characterNew').toLocaleLowerCase()}`}
              </div>
            </>
          )}
        </div>

        {/* lock credits */}
        {renderContainerLockCredits()}

        {/* custom package */}
        {userUseCustomPackage && (
          <div className="package-info-item">
            <div className="package-info">
              {t('remainCurrentPlanCharacters')}
            </div>
            <div className="package-info-value">
              {isExpiredPackage
                ? 0
                : `${delimitNumber(remainingCustomCredits, language)} ${t(
                    'characterNew',
                  ).toLocaleLowerCase()}`}
            </div>
          </div>
        )}

        {/* old packages */}
        {(remainingCharacters + bonusCharacters > 0 ||
          lockCharacters > 0 ||
          userUseOneTimePackage) && (
          <div className="package-info-item">
            <div className="package-info">
              {!userUseOneTimePackage
                ? t('remainOldCharacters')
                : t('numberCharacters')}
            </div>
            <div
              className={
                (isExpiredPackage && lockCharacters === 0) ||
                (!isExpiredPackage && totalOldRemainingCharacters === 0)
                  ? 'package-info-value-expired'
                  : 'package-info-value'
              }
            >
              {isExpiredPackage
                ? `${delimitNumber(lockCharacters, language)} ${t(
                    'characterNew',
                  )}`
                : `${delimitNumber(totalOldRemainingCharacters, language)} ${t(
                    'characterNew',
                  )}`}

              {isExpiredPackage && userUseOneTimePackage && (
                <LockRounded
                  sx={{
                    height: 14,
                    width: 14,
                    fontSize: 14,
                    color: '#FF3B30',
                  }}
                  className="lock-icon"
                />
              )}
            </div>
          </div>
        )}

        {/* package expiry date */}
        <div className="package-info-item">
          <div className="package-info">{t('expiryDate')}</div>
          <div
            className={
              packageExpiryDate && isExpiredPackage
                ? 'package-info-value-expired'
                : 'package-info-value'
            }
          >
            {getPackageExpired()}
          </div>
        </div>
        {lockCharacters > 0 && user?.preservationExpiryDate && (
          <div className="package-info-item">
            <div className="package-info">{t('preservedCharactersDateV2')}</div>
            <div className="package-info-value">
              {moment(user?.preservationExpiryDate).format(FORMAT_DATETIME)}
            </div>
          </div>
        )}
      </div>
      {isShowUpgradeButton && renderPackageButton()}
    </NewUserInformationNoteWrapper>
  );
};

export default MultipleCreditInfo;
